import { observable, action } from 'mobx';
import api from 'core/util/api';
import { showErrorToast, showWarningToast } from 'core/components/toast';
import { MCP_AUTH_TYPE, MCP_OAUTH_BROADCAST_CHANNEL } from '@kentik/ui-shared/kaizen/constants';
import { openMcpOAuthPopup } from 'app/util/mcpOAuthPopup';
import McpServerCollection from './McpServerCollection';

const STATUS_COOLDOWN_MS = 60000;

function showConnectFailure(err, serverName) {
  if (err.refusal) {
    return showErrorToast(err.refusal);
  }

  if (err.cancelled) {
    return showWarningToast('Sign-in was not completed.');
  }

  if (err.timedOut) {
    return showWarningToast('Sign-in timed out.');
  }

  return showErrorToast(`Couldn't connect to ${serverName}. Try again.`);
}

class McpServerStore {
  collection = new McpServerCollection();

  @observable statusMap = new Map();

  @observable oauthStatusMap = new Map();

  @observable loadError = null;

  lastStatusFetchAt = 0;

  statusGeneration = 0;

  // A sign-in completed in any tab changes this user's grant.
  constructor() {
    if (typeof BroadcastChannel !== 'undefined') {
      this.oauthChannel = new BroadcastChannel(MCP_OAUTH_BROADCAST_CHANNEL);
      this.oauthChannel.unref?.();
      this.oauthChannel.onmessage = ({ data }) => {
        if (data?.type === 'mcp-oauth-complete' && data.serverId) {
          this.fetchOAuthStatus(data.serverId);
        }
      };
    }
  }

  @action
  _setLoadError = (message) => {
    this.loadError = message;
  };

  @action
  _applyStatuses = (statuses) => {
    this.statusMap.replace(new Map(Object.entries(statuses || {})));
  };

  @action
  _failStatuses = () => {
    this.collection.get().forEach((model) => {
      this.statusMap.set(model.get('id'), { connected: false, error: 'Request failed' });
    });
  };

  @action
  _setOAuthStatus = (serverId, status) => {
    this.oauthStatusMap.set(serverId, status);
  };

  @action
  _updateOAuthStatus = (serverId, fields) => {
    this.oauthStatusMap.set(serverId, { ...this.oauthStatusMap.get(serverId), ...fields });
  };

  markOAuthAuthRequired = (serverId) => {
    this._updateOAuthStatus(serverId, { connected: false, needs_reauth: true });
  };

  loadServersWithStatus = () => {
    this._setLoadError(null);

    return this.collection
      .fetch()
      .then(() => Promise.all([this.checkAllStatuses(), this.fetchAllOAuthStatuses()]))
      .catch((err) => {
        this._setLoadError(err.message || 'Could not load MCP servers');
      });
  };

  checkAllStatuses = (opts = {}) => {
    if (!opts.force && Date.now() - this.lastStatusFetchAt < STATUS_COOLDOWN_MS) {
      return Promise.resolve();
    }

    this.lastStatusFetchAt = Date.now();
    this.statusGeneration += 1;
    const generation = this.statusGeneration;

    return api
      .get(`/api/ui/ai/mcp-servers/status${opts.force ? '?force=true' : ''}`, { showErrorToast: false })
      .then((statuses) => {
        if (generation === this.statusGeneration) {
          this._applyStatuses(statuses);
        }
      })
      .catch(() => {
        if (generation === this.statusGeneration) {
          this._failStatuses();
        }
      });
  };

  fetchAllOAuthStatuses = () => {
    const oauthIds = this.collection
      .get()
      .filter((model) => model.get('auth_type') === MCP_AUTH_TYPE.OAUTH)
      .map((model) => model.get('id'));

    this.pruneOAuthStatuses(oauthIds);

    return Promise.all(oauthIds.map((id) => this.fetchOAuthStatus(id)));
  };

  @action
  pruneOAuthStatuses = (keepIds) => {
    const keep = new Set(keepIds);

    for (const key of this.oauthStatusMap.keys()) {
      if (!keep.has(key)) {
        this.oauthStatusMap.delete(key);
      }
    }
  };

  discoverTools = (payload) => api.post('/api/ui/ai/mcp-servers/discover', { data: payload });

  configureServerOAuth = (serverId, payload) =>
    api.post(`/api/ui/ai/mcp-servers/${serverId}/oauth/configure`, { data: payload });

  // Connection status is derived from the grant, so it has to be re-read whenever the grant changes.
  refreshAfterOAuthChange = (serverId) =>
    Promise.all([this.checkAllStatuses({ force: true }), this.fetchOAuthStatus(serverId)]);

  // Stays connecting until the refreshed status replaces it; popup success alone never reads as connected.
  connectOAuth = (serverId, { serverName, pause }) => {
    this._updateOAuthStatus(serverId, { connecting: true, authorizeUrl: null });

    return openMcpOAuthPopup(serverId, {
      pause,
      onBlocked: (url) => {
        this._updateOAuthStatus(serverId, { authorizeUrl: url });
      }
    })
      .then(() => this.refreshAfterOAuthChange(serverId).then(() => ({ connected: true, serverId })))
      .catch((err) => {
        this._updateOAuthStatus(serverId, { connecting: false, authorizeUrl: null });
        showConnectFailure(err, serverName);

        return { connected: false, serverId };
      });
  };

  fetchOAuthStatus = (serverId) =>
    api
      .get(`/api/ui/ai/mcp-servers/${serverId}/oauth/status`, { showErrorToast: false, suppressNotFoundErrors: true })
      .then((result) => {
        this._setOAuthStatus(serverId, result);

        return result;
      })
      .catch(() => {
        this._setOAuthStatus(serverId, { connected: false });

        return { connected: false };
      });

  disconnectOAuth = (serverId) =>
    api.del(`/api/ui/ai/mcp-servers/${serverId}/oauth/disconnect`, { showErrorToast: false }).then(
      () => {
        this._setOAuthStatus(serverId, { connected: false });

        return this.refreshAfterOAuthChange(serverId);
      },
      () => {
        showErrorToast("Couldn't disconnect. Try again.");
      }
    );
}

export default new McpServerStore();
